import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/StatusBadge";
import { DeadlineBadge } from "@/components/DeadlineBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MapPin, ChevronRight, ChevronLeft, ClipboardCheck, AlertCircle, Search, X } from "lucide-react";

const STATUS_OPTS = [
  { v: "all", l: "All statuses" },
  { v: "assigned", l: "Assigned" },
  { v: "in_progress", l: "In Progress" },
  { v: "not_served", l: "Attempted / Not served" },
  { v: "pending_review", l: "Pending Review" },
  { v: "completed", l: "Completed" },
];
const REASSIGN_OPTS = [
  { v: "all", l: "My jobs (all)" },
  { v: "reassigned", l: "My jobs · was reassigned" },
  { v: "never", l: "My jobs · never reassigned" },
  { v: "reassigned_away", l: "Reassigned away (history)" },
];

function JobList({ scope, activeOnly = false, title, badge, showAccept = false, showReassignment = false }) {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [term, setTerm] = useState("");
  const [status, setStatus] = useState("all");
  const [svc, setSvc] = useState("all");
  const [reassignment, setReassignment] = useState("all");
  const [sort, setSort] = useState("desc");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0, page: 1, pages: 1, service_types: [] });
  const [loading, setLoading] = useState(true);
  const tid = scope === "available" ? "available" : (activeOnly ? "active" : "history");

  useEffect(() => {
    let active = true;
    setLoading(true);
    api.get("/server/jobs", { params: { scope, active_only: activeOnly, q: term, status, service_type: svc, reassignment, sort, date_from: dateFrom, date_to: dateTo, page, page_size: 25 } })
      .then(({ data }) => { if (active) setData(data); })
      .catch(() => { if (active) setData({ items: [], total: 0, page: 1, pages: 1, service_types: [] }); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [scope, activeOnly, term, status, svc, reassignment, sort, dateFrom, dateTo, page]);

  const reset = (fn) => (v) => { setPage(1); fn(v); };
  const clearAll = () => { setQ(""); setTerm(""); setStatus("all"); setSvc("all"); setReassignment("all"); setSort("desc"); setDateFrom(""); setDateTo(""); setPage(1); };

  return (
    <div className="rounded-xl border border-slate-200 bg-white" data-testid={`server-${tid}-panel`}>
      <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
        <h3 className="font-display text-lg font-bold text-slate-900">{title}</h3>
        {badge && <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-700" data-testid={`server-${tid}-count-badge`}>{(data.total || 0).toLocaleString()} {badge}</span>}
      </div>

      <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-5 py-3">
        <form className="relative min-w-48 flex-1" onSubmit={(e) => { e.preventDefault(); setPage(1); setTerm(q); }}>
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input data-testid={`server-${tid}-search`} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search job #, defendant, plaintiff, case #, address, client… (Enter)" className="h-9 pl-9" />
        </form>
        <Select value={status} onValueChange={reset(setStatus)}>
          <SelectTrigger data-testid={`server-${tid}-status`} className="h-9 w-40"><SelectValue /></SelectTrigger>
          <SelectContent>{STATUS_OPTS.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
        </Select>
        {showReassignment && (
          <Select value={reassignment} onValueChange={reset(setReassignment)}>
            <SelectTrigger data-testid={`server-${tid}-reassignment`} className="h-9 w-52"><SelectValue /></SelectTrigger>
            <SelectContent>{REASSIGN_OPTS.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
          </Select>
        )}
        <Select value={svc} onValueChange={reset(setSvc)}>
          <SelectTrigger data-testid={`server-${tid}-service-type`} className="h-9 w-40"><SelectValue placeholder="Service type" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All service types</SelectItem>
            {(data.service_types || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input data-testid={`server-${tid}-date-from`} type="date" value={dateFrom} onChange={(e) => { setPage(1); setDateFrom(e.target.value); }} className="h-9 w-full sm:w-36" />
        <Input data-testid={`server-${tid}-date-to`} type="date" value={dateTo} onChange={(e) => { setPage(1); setDateTo(e.target.value); }} className="h-9 w-full sm:w-36" />
        <Button data-testid={`server-${tid}-sort`} variant="outline" size="sm" onClick={() => { setPage(1); setSort(sort === "desc" ? "asc" : "desc"); }}>
          {sort === "desc" ? "Newest → Oldest" : "Oldest → Newest"}
        </Button>
        <Button data-testid={`server-${tid}-clear`} variant="ghost" size="sm" onClick={clearAll}>Clear all</Button>
      </div>

      <div className="px-5 py-2 text-xs text-slate-500" data-testid={`server-${tid}-count`}>
        <span className="font-semibold text-slate-900">{(data.total || 0).toLocaleString()}</span> job(s){sort === "desc" ? " · Newest → Oldest" : " · Oldest → Newest"}
      </div>

      {loading ? (
        <p className="px-5 py-10 text-center text-sm text-slate-400">Loading…</p>
      ) : data.items.length === 0 ? (
        <p className="px-5 py-10 text-center text-sm text-slate-400">No jobs match.</p>
      ) : (
        data.items.map((o) => (
          <div key={o.id} data-testid={`job-row-${o.job_number}`} className="flex items-center justify-between gap-4 border-b border-slate-100 px-5 py-4 last:border-0">
            <button onClick={() => nav(`/server/jobs/${o.id}`)} className="min-w-0 flex-1 text-left">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono-arps text-sm font-semibold text-slate-900">#{o.job_number}</span>
                <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-medium text-slate-500">{o.service_type}</span>
                {o.reassigned && <span data-testid={`reassigned-badge-${o.job_number}`} className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700" title={`Originally ${o.original_server_name || "—"} → ${o.current_server_name || "—"}`}>Reassigned</span>}
              </div>
              {scope === "available" ? (
                <p className="mt-0.5 flex items-center gap-1 text-xs text-slate-400"><MapPin className="h-3 w-3" /> {o.service_area || o.county || "Area shown after you accept"}</p>
              ) : (
                <>
                  <p className="mt-0.5 truncate text-sm text-slate-600">{o.defendant}</p>
                  <p className="mt-0.5 flex items-center gap-1 text-xs text-slate-400"><MapPin className="h-3 w-3" /> {o.service_address?.street}, {o.service_address?.city}, {o.service_address?.state}</p>
                </>
              )}
            </button>
            <div className="flex items-center gap-3">
              <div className="flex flex-col items-end gap-1.5">
                <StatusBadge status={o.status} pulse={["in_progress", "assigned"].includes(o.status)} />
                {o.status !== "completed" && <DeadlineBadge status={o.deadline_status} days={o.days_to_deadline} hideLowRisk />}
              </div>
              <Button data-testid={`open-job-${o.job_number}`} size="sm" variant="outline" onClick={() => nav(`/server/jobs/${o.id}`)}>
                {showAccept ? "View & accept" : "Open"} <ChevronRight className="ml-1 h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        ))
      )}

      {data.pages > 1 && (
        <div className="flex items-center justify-center gap-3 border-t border-slate-100 py-3">
          <Button data-testid={`server-${tid}-prev`} variant="outline" size="sm" disabled={data.page <= 1} onClick={() => setPage(data.page - 1)}><ChevronLeft className="mr-1 h-4 w-4" /> Prev</Button>
          <span className="text-sm text-slate-500">Page {data.page} / {data.pages}</span>
          <Button data-testid={`server-${tid}-next`} variant="outline" size="sm" disabled={data.page >= data.pages} onClick={() => setPage(data.page + 1)}>Next <ChevronRight className="ml-1 h-4 w-4" /></Button>
        </div>
      )}
    </div>
  );
}

function AvailableByZip() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState({});
  useEffect(() => { api.get("/server/available-jobs/by-zip").then(({ data }) => setData(data)).catch(() => setData({ zips: [] })); }, []);
  if (!data) return null;
  const fmt = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
  return (
    <div className="rounded-xl border border-slate-200 bg-white" data-testid="available-by-zip-panel">
      <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
        <h3 className="font-display text-lg font-bold text-slate-900">Available jobs by ZIP <span className="text-sm font-normal text-slate-400">(highest potential first)</span></h3>
        <span className="rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-semibold text-emerald-700" data-testid="zip-total-potential">{data.total_jobs || 0} jobs · {fmt(data.total_potential)}</span>
      </div>
      {(!data.zips || data.zips.length === 0) ? (
        <p className="px-5 py-8 text-center text-sm text-slate-400">No available jobs right now.</p>
      ) : (
        <div className="divide-y divide-slate-100">
          {data.zips.map((g) => (
            <div key={g.zip} data-testid={`zip-group-${g.zip}`}>
              <button onClick={() => setOpen((s) => ({ ...s, [g.zip]: !s[g.zip] }))} className="flex w-full items-center justify-between gap-4 px-5 py-3.5 text-left transition hover:bg-slate-50">
                <div className="flex items-center gap-3">
                  <ChevronRight className={`h-4 w-4 text-slate-400 transition-transform ${open[g.zip] ? "rotate-90" : ""}`} />
                  <span className="font-mono-arps text-sm font-semibold text-slate-900" data-testid={`zip-code-${g.zip}`}>{g.zip}</span>
                  <span className="text-sm text-slate-500" data-testid={`zip-jobs-${g.zip}`}>{g.job_count} job{g.job_count === 1 ? "" : "s"}</span>
                </div>
                <span className="font-semibold text-emerald-700" data-testid={`zip-comp-${g.zip}`}>{fmt(g.potential_compensation)}</span>
              </button>
              {open[g.zip] && (
                <div className="bg-slate-50/60 px-5 pb-3">
                  {g.jobs.map((j) => (
                    <div key={j.id} data-testid={`zip-job-${j.job_number}`} className="flex items-center justify-between gap-4 border-t border-slate-100 py-2.5">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-mono-arps text-sm font-semibold text-slate-900">#{j.job_number}</span>
                          <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] text-slate-500">{j.service_type}</span>
                        </div>
                        <p className="mt-0.5 truncate text-xs text-slate-500">{j.city}, {j.state}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-semibold text-emerald-700" data-testid={`job-comp-${j.job_number}`}>{fmt(j.compensation)}</span>
                        <Button data-testid={`zip-accept-${j.job_number}`} size="sm" variant="outline" onClick={() => nav(`/server/jobs/${j.id}`)}>View &amp; accept <ChevronRight className="ml-1 h-3.5 w-3.5" /></Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function NotarySection() {
  const nav = useNavigate();
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/notary/portal/assignments").then(({ data }) => setRows(data)).catch(() => setRows([])); }, []);
  return (
    <div className="rounded-xl border border-slate-200 bg-white" data-testid="notary-section-panel">
      <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
        <h3 className="font-display text-lg font-bold text-slate-900">Notary assignments</h3>
        <span className="rounded-full bg-indigo-100 px-2.5 py-0.5 text-xs font-semibold text-indigo-700">{(rows || []).length} assigned</span>
      </div>
      {rows === null ? (
        <p className="px-5 py-8 text-center text-sm text-slate-400">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="px-5 py-8 text-center text-sm text-slate-400" data-testid="notary-section-empty">No notary assignments yet. Admin-assigned notarizations (after pricing is confirmed) will appear here.</p>
      ) : (
        rows.map((r) => (
          <div key={r.id} data-testid={`notary-row-${r.id}`} className="flex items-center justify-between gap-4 border-b border-slate-100 px-5 py-4 last:border-0">
            <button onClick={() => nav(`/server/notary/${r.id}`)} className="min-w-0 flex-1 text-left">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded bg-indigo-100 px-1.5 py-0.5 text-[11px] font-semibold text-indigo-700">Notary</span>
                <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] text-slate-500">{r.service_type || "Notary"}</span>
                <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] text-slate-500">{r.status}</span>
              </div>
              <p className="mt-0.5 flex items-center gap-1 text-xs text-slate-400"><MapPin className="h-3 w-3" /> {r.county || "—"} · {r.num_signers || 1} signer(s) · {r.document_category || "—"}</p>
            </button>
            <Button size="sm" variant="outline" onClick={() => nav(`/server/notary/${r.id}`)}>Open <ChevronRight className="ml-1 h-3.5 w-3.5" /></Button>
          </div>
        ))
      )}
    </div>
  );
}

function ServiceCountiesNudge({ user }) {
  const nav = useNavigate();
  const notary = ["notary", "both"].includes(user?.service_capability);
  const key = `arps_hide_county_nudge_${user?.id || user?.email || ""}`;
  const [hidden, setHidden] = useState(() => {
    try { return localStorage.getItem(key) === "1"; } catch { return false; }
  });
  const [hasCounties, setHasCounties] = useState(null); // null = loading

  useEffect(() => {
    let active = true;
    api.get("/provider/service-counties")
      .then(({ data }) => { if (active) setHasCounties((data.counties || []).length > 0); })
      .catch(() => { if (active) setHasCounties(true); }); // fail safe: don't nag on error
    return () => { active = false; };
  }, []);

  // Optional-only: never render while loading, once counties are set, or after dismissal.
  if (hidden || hasCounties === null || hasCounties) return null;

  const dismiss = (e) => {
    e.stopPropagation();
    try { localStorage.setItem(key, "1"); } catch { /* noop */ }
    setHidden(true);
  };

  return (
    <div data-testid="county-nudge-card"
      className="relative flex w-full items-center gap-3 rounded-xl border border-indigo-200 bg-indigo-50 p-4 text-left">
      <MapPin className="h-5 w-5 shrink-0 text-indigo-600" />
      <div className="flex-1">
        <p className="font-semibold text-indigo-900">
          {notary ? "Add the counties you prefer to serve" : "Add the counties you serve"}
        </p>
        <p className="text-sm text-indigo-700">
          Optional — providers who list their counties get {notary ? "notary" : "local"} jobs there first.
          It takes a minute and never affects your approval.
        </p>
      </div>
      <Button data-testid="county-nudge-add" size="sm" onClick={() => nav("/server/onboarding")}
        className="shrink-0 bg-indigo-600 text-white hover:bg-indigo-700">
        Add counties <ChevronRight className="ml-0.5 h-3.5 w-3.5" />
      </Button>
      <button data-testid="county-nudge-dismiss" onClick={dismiss} aria-label="Dismiss"
        className="ml-1 shrink-0 rounded-md p-1 text-indigo-400 transition hover:bg-indigo-100 hover:text-indigo-700">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

export default function ServerDashboard({ historyMode = false }) {
  const nav = useNavigate();
  const { user } = useAuth();
  const [onboarding, setOnboarding] = useState(null);

  useEffect(() => {
    api.get("/server/onboarding").then(({ data }) => setOnboarding(data)).catch(() => {});
  }, []);

  // Notary-only providers stay here and work from the unified Assignments view below.

  const onboardingIncomplete = onboarding && (!onboarding.account.complete || !onboarding.documents_complete);
  const psElig = user?.process_server_eligible ?? ["process_server", "both"].includes(user?.service_capability);
  const notElig = user?.notary_eligible ?? ["notary", "both"].includes(user?.service_capability);

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div>
        {user?.agency_name && <p data-testid="dashboard-business-name" className="text-sm font-semibold text-emerald-700">{user.agency_name}</p>}
        <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900">{historyMode ? "Job history" : "Assignments"}</h2>
        <p className="text-slate-500">{historyMode ? "Every job you've worked." : "Available opportunities and your active jobs across the services you're approved for."}</p>
      </div>

      {!historyMode && onboardingIncomplete && (
        <button data-testid="onboarding-banner" onClick={() => nav("/server/onboarding")}
          className="flex w-full items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-left transition hover:shadow-md">
          <AlertCircle className="h-5 w-5 shrink-0 text-amber-600" />
          <div className="flex-1">
            <p className="font-semibold text-amber-900">Complete your onboarding</p>
            <p className="text-sm text-amber-700">
              {!onboarding.account.complete ? "Your account profile is incomplete. " : ""}
              {onboarding.summary.approved}/{onboarding.total_documents} required documents approved.
            </p>
          </div>
          <span className="flex items-center gap-1 text-sm font-semibold text-amber-800"><ClipboardCheck className="h-4 w-4" /> Open</span>
        </button>
      )}

      {!historyMode && (psElig || notElig) && <ServiceCountiesNudge user={user} />}

      {historyMode ? (
        <JobList scope="mine" title="All my jobs" showReassignment />
      ) : (
        <>
          {!psElig && !notElig && (
            <div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-slate-500" data-testid="assignments-empty-all">
              No available assignments in your approved services yet. Complete onboarding and admin approval to start receiving Process Server and Notary opportunities.
            </div>
          )}
          {psElig && (
            <>
              <AvailableByZip />
              <JobList scope="available" title="Available process-server jobs" badge="open" showAccept />
              <JobList scope="mine" title="My process-server jobs" showReassignment />
            </>
          )}
          {notElig && <NotarySection />}
        </>
      )}
    </div>
  );
}
